import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X, ShieldCheck, BarChart3 } from "lucide-react";
import { getConsent, saveConsent, initAnalytics } from "../lib/consent";

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      if (existing.analytics) initAnalytics();
    } else {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      setAnalytics(!!getConsent()?.analytics);
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("avd:open-cookie-settings", open);
    return () => window.removeEventListener("avd:open-cookie-settings", open);
  }, []);

  const close = () => {
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => {
    saveConsent({ analytics: true });
    close();
  };

  const necessaryOnly = () => {
    saveConsent({ analytics: false });
    close();
  };

  const savePrefs = () => {
    saveConsent({ analytics });
    close();
  };

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {visible && !showPrefs && (
          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 48, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            data-testid="cookie-banner"
            role="dialog"
            aria-label="Consimțământ cookie-uri"
            className="fixed z-[95] bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md"
          >
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/70 p-5 sm:p-6 text-slate-600">
              <div className="flex items-start gap-3.5">
                <span className="grid place-items-center shrink-0 w-10 h-10 rounded-xl bg-[#1565C0]/10 text-[#1565C0]">
                  <Cookie size={20} />
                </span>
                <div>
                  <h3 className="font-display font-bold text-slate-900 text-[15px]">Cookie-uri pe Auto Valy Drive</h3>
                  <p className="mt-1.5 text-sm leading-relaxed">
                    Folosim cookie-uri pentru funcționarea site-ului și pentru îmbunătățirea experienței tale. Poți accepta toate cookie-urile sau îți poți personaliza preferințele.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-all-btn"
                  className="flex-1 inline-flex items-center justify-center bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
                >
                  Accept toate
                </button>
                <button
                  onClick={necessaryOnly}
                  data-testid="cookie-necessary-btn"
                  className="flex-1 inline-flex items-center justify-center border border-slate-300 hover:border-[#1565C0] text-slate-700 hover:text-[#1565C0] rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
                >
                  Doar necesare
                </button>
                <button
                  onClick={() => setShowPrefs(true)}
                  data-testid="cookie-preferences-btn"
                  className="inline-flex items-center justify-center text-[#1565C0] hover:underline rounded-full px-4 py-2.5 text-sm font-semibold"
                >
                  Preferințe
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-400">
                Detalii în{" "}
                <Link to="/politica-cookies" data-testid="cookie-banner-policy-link" className="underline underline-offset-2 hover:text-[#1565C0]">
                  Politica Cookies
                </Link>
                .
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences modal */}
      <AnimatePresence>
        {visible && showPrefs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="cookie-preferences-modal"
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setShowPrefs(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200/70 p-6 text-slate-600"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-slate-900">Preferințe cookie-uri</h3>
                <button
                  onClick={() => setShowPrefs(false)}
                  data-testid="cookie-preferences-close"
                  aria-label="Închide"
                  className="grid place-items-center w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
                  <input type="checkbox" checked disabled data-testid="cookie-necessary-toggle" className="mt-1 w-4 h-4 accent-[#1565C0]" />
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
                      <ShieldCheck size={15} className="text-[#1565C0]" /> Cookie-uri necesare
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#1565C0] bg-[#1565C0]/10 rounded-full px-2 py-0.5">Mereu active</span>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed">Esențiale pentru funcționarea site-ului: reținerea alegerii tale privind cookie-urile și preferințele de afișare.</p>
                  </div>
                </div>

                <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-[#1565C0]/40 transition-colors">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    data-testid="cookie-analytics-toggle"
                    className="mt-1 w-4 h-4 accent-[#1565C0]"
                  />
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
                      <BarChart3 size={15} className="text-[#1565C0]" /> Cookie-uri analiză
                    </div>
                    <p className="mt-1 text-xs leading-relaxed">Ne ajută să înțelegem cum este folosit site-ul (ex. Google Analytics), pentru a îmbunătăți conținutul. Sunt activate doar cu acordul tău.</p>
                  </div>
                </label>
              </div>

              <div className="mt-5 flex gap-2">
                <button
                  onClick={savePrefs}
                  data-testid="cookie-save-preferences-btn"
                  className="flex-1 inline-flex items-center justify-center bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
                >
                  Salvează preferințele
                </button>
                <button
                  onClick={() => setShowPrefs(false)}
                  data-testid="cookie-preferences-back-btn"
                  className="inline-flex items-center justify-center border border-slate-300 text-slate-700 rounded-full px-5 py-2.5 text-sm font-semibold hover:border-[#1565C0] hover:text-[#1565C0] transition-colors"
                >
                  Înapoi
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
